import React, { useMemo, useState } from 'react';
import { useLazyQuery, useQuery } from '@common/hooks';
import { useCurrentEntity } from '@identity/hooks';
import { filter, isEqual, negate } from 'lodash';
import {
  DISPUTE_LABEL_OVERWRITE,
  DisputeActionsDropdown,
  DisputeBulkActionFooter,
  DisputeReviewLink,
  DisputeState,
  DisputeTypeDisplay,
  TotalCountLabel,
} from '@pay-bill/components';
import { toUSCompactDateFormat } from 'works/legacy/lib/date-helper';
import { Colors, Spacing } from 'works/legacy/lib/style-helper';
import { ErrorHandler, Text, TextFilterSearch } from '@common/components';
import {
  RenderFilteredTable,
  RenderTableColumn,
  RenderTableOnChange,
} from 'works/components/filtered-table/render-filtered-table';
import { useFilterCallbacks } from 'works/legacy/lib/hooks/use-filter-callbacks';
import {
  inMemoryStore,
  localStorageStore,
  useStoredFilters,
} from 'works/legacy/lib/hooks/use-stored-filters';
import {
  DISPUTE_FILTERS_QUERY,
  DisputeListItemFragmentType,
  PAGINATED_DISPUTES_QUERY,
  PaginatedDisputesQueryArgs,
} from '@pay-bill/graphql';
import {
  CLEANUP_WHEN_EMPTY,
  getRedirectTargetEntity,
  nonFalse,
} from '@pay-bill/services';
import {
  buildOptionsFromData,
  MultiSelectTableFilter,
} from 'works/components/filtered-table';
import { SUPPLIER_NAMES_QUERY } from '@institutions/graphql';

const notExported = (value: string) => value === 'ONLY_NOT_EXPORTED';

const nonNotExported = negate(notExported);

export const BillingDisputeList = ({
  viewMode,
  additionalFilters,
  selectable,
}: {
  viewMode?: 'revInvoiceDetails' | 'invoiceDetails';
  additionalFilters?: PaginatedDisputesQueryArgs['filters'];
  selectable?: boolean;
}) => {
  const currentEntity = useCurrentEntity();
  const entityType = currentEntity.entityType;

  const [filters, setFilters] = useStoredFilters('DisputeTable', {
    store: viewMode === 'revInvoiceDetails' ? inMemoryStore : localStorageStore,
    defaults: {} as PaginatedDisputesQueryArgs['filters'],
  });

  const { onClearFilter, onClearAllFilters } = useFilterCallbacks(setFilters);

  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  const { data: filterOptions } = useQuery(DISPUTE_FILTERS_QUERY);

  const [fetchSupplier, { error: supplierError, data: supplierData }] =
    useLazyQuery(SUPPLIER_NAMES_QUERY);

  const [pageData, setPageData] = useState(() => ({
    limit: 50,
    offset: 0,
    orderDirection: 'DESC' as PaginatedDisputesQueryArgs['orderDirection'],
    orderField: 'NEEDED_ACTIONS' as PaginatedDisputesQueryArgs['orderField'],
  }));

  const { data, error, loading } = useQuery(PAGINATED_DISPUTES_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      ...pageData,
      filters: {
        ...filters,
        state: filters.state?.filter(nonNotExported),
        exported: filters.state?.find(notExported) ? false : undefined,
        ...additionalFilters,
      },
    },
    onCompleted() {
      fetchSupplier();
    },
  });

  const statesFilter = filterOptions?.disputeFilters?.states || [];

  const disputesColumns = filter<
    RenderTableColumn<DisputeListItemFragmentType> | false,
    RenderTableColumn<DisputeListItemFragmentType>
  >(
    [
      {
        title: 'Dispute Code',
        key: 'code',
        className: 'dispute-code-col',
        sorter: true,
        filtered: !!filters.code?.length,
        filteredValue: filters.code || CLEANUP_WHEN_EMPTY,
        filterFormat: 'id',
        filterDropdown: ({ setSelectedKeys, confirm }) => (
          <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
            <TextFilterSearch
              autoFocus
              id="filter-by-dispute-code-input"
              placeHolder="Filter by code"
              onSearch={(value) => {
                setSelectedKeys(value ? [value] : []);
                confirm();
              }}
            />
          </section>
        ),
        render: (_: unknown, dispute: DisputeListItemFragmentType) => (
          <DisputeReviewLink disputeCode={dispute.code} />
        ),
      },
      {
        title: 'Type',
        key: 'disputeType',
        dataIndex: 'disputeType',
        className: 'type-col',
        sorter: true,
        render: (type) => <DisputeTypeDisplay type={type} />,
      },
      viewMode !== 'revInvoiceDetails' &&
        entityType === 'Supplier' && {
          title: 'Reverse Invoice #',
          key: 'revInvoiceCode',
          dataIndex: 'worksReverseInvoice',
          className: 'rev-invoice-code-col',
          width: 200,
          filtered: !!filters.revInvoiceCode?.length,
          filteredValue: filters.revInvoiceCode || CLEANUP_WHEN_EMPTY,
          sorter: true,
          filterFormat: 'id',
          render: (item) => <div>{item.code}</div>,
          filterDropdown: ({ setSelectedKeys, confirm }) => (
            <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
              <TextFilterSearch
                autoFocus
                id="filter-by-rev-invoice-code-input"
                placeHolder="Filter by reverse invoice code"
                onSearch={(value) => {
                  setSelectedKeys(value ? [value] : []);
                  confirm();
                }}
              />
            </section>
          ),
        },
      !viewMode &&
        entityType !== 'Supplier' && {
          title: 'Invoice #',
          key: 'invoiceCode',
          dataIndex: 'worksInvoice',
          className: 'invoice-code-col',
          width: 200,
          filtered: !!filters.invoiceCode?.length,
          filteredValue: filters.invoiceCode || CLEANUP_WHEN_EMPTY,
          sorter: true,
          filterFormat: 'id',
          render: (item) => <div>{item.code}</div>,
          filterDropdown: ({ setSelectedKeys, confirm }) => (
            <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
              <TextFilterSearch
                autoFocus
                id="filter-by-invoice-code-input"
                placeHolder="Filter by invoice code"
                onSearch={(value) => {
                  setSelectedKeys(value ? [value] : []);
                  confirm();
                }}
              />
            </section>
          ),
        },
      {
        title: 'Date Worked',
        key: 'date',
        dataIndex: 'newDate',
        className: 'date-col',
        sorter: true,
        render: (date) => <div>{toUSCompactDateFormat(date)}</div>,
      },
      {
        title: 'EEID',
        key: 'employeeId',
        dataIndex: 'employeeId',
        className: 'employee-id-col',
        filterFormat: 'id',
        render: (employeeId) =>
          employeeId ? (
            <div>{employeeId}</div>
          ) : (
            <i style={{ color: Colors.steelDarker }}>Blank</i>
          ),
        sorter: true,
        filtered: !!filters.employeeId?.length,
        filteredValue: filters.employeeId || CLEANUP_WHEN_EMPTY,
        filterDropdown: ({ setSelectedKeys, confirm }) => (
          <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
            <TextFilterSearch
              autoFocus
              id="filter-by-employee-id-input"
              placeHolder="Filter by EEID"
              onSearch={(value) => {
                setSelectedKeys(value ? [value] : []);
                confirm();
              }}
            />
          </section>
        ),
      },
      {
        title: 'Full Name',
        key: 'employeeName',
        className: 'employee-name-col sentry-mask',
        sorter: true,
        filtered: !!filters.employeeName?.length,
        filteredValue: filters.employeeName || CLEANUP_WHEN_EMPTY,
        filterFormat: 'id',
        filterDropdown: ({ setSelectedKeys, confirm }) => (
          <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
            <TextFilterSearch
              autoFocus
              id="filter-by-employee-name-input"
              placeHolder="Filter by name"
              onSearch={(value) => {
                setSelectedKeys(value ? [value] : []);
                confirm();
              }}
            />
          </section>
        ),
        render: (_: unknown, dispute: DisputeListItemFragmentType) => (
          <div>{dispute.staffingRequestAssignment.worksProfile.fullName}</div>
        ),
      },
      entityType !== 'Supplier' && {
        title: 'CC',
        key: 'costCenter',
        dataIndex: 'organizationUnit',
        sorter: true,
        filtered: !!filters.costCenter?.length,
        filteredValue: filters.costCenter || CLEANUP_WHEN_EMPTY,
        filterDropdown: ({ setSelectedKeys, confirm }) => (
          <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
            <TextFilterSearch
              onSearch={(value) => {
                value ? setSelectedKeys([value]) : setSelectedKeys([]);
                confirm();
              }}
            />
          </section>
        ),
        render: (organizationUnit) => <div>{organizationUnit.costCenter}</div>,
      },
      entityType !== 'Supplier' && {
        title: 'Supplier',
        key: 'supplierName',
        dataIndex: 'supplier',
        className: 'supplier-name-col',
        filtered: !!filters.supplierName?.length,
        filteredValue: filters.supplierName || CLEANUP_WHEN_EMPTY,
        filterDropdown: MultiSelectTableFilter,
        filters: supplierData && buildOptionsFromData(supplierData.suppliers),
        render: (supplier) => <div>{supplier.name}</div>,
        sorter: true,
      },
      {
        title: 'Status',
        key: 'state',
        width: '200px',
        dataIndex: 'state',
        className: 'state-col',
        sorter: true,
        filtered: !!filters.state?.length,
        filteredValue: filters.state || CLEANUP_WHEN_EMPTY,
        filters: [
          ...statesFilter.map((filter) => ({
            text: DISPUTE_LABEL_OVERWRITE[filter] || filter,
            value: filter,
          })),
          {
            text: <i>Only include items that haven&apos;t been exported</i>,
            labelText: "Only include items that haven't been exported",
            value: 'ONLY_NOT_EXPORTED',
          },
        ],
        render: (text, dispute) => (
          <div>
            <DisputeState
              state={text}
              createdBy={getRedirectTargetEntity(dispute.createdBy?.entityType)}
            />
            {dispute.exported && (
              <div>
                <Text color="midnightLighter" size="sm">
                  Exported
                </Text>
              </div>
            )}
          </div>
        ),
      },
      {
        title: 'Last Updated',
        key: 'lastActionAt',
        dataIndex: 'lastActionAt',
        className: 'last-updated-col',
        sorter: true,
        render: (date) => <div>{toUSCompactDateFormat(date)}</div>,
      },
      {
        title: 'Action',
        render: (_: unknown, dispute: DisputeListItemFragmentType) => (
          <DisputeActionsDropdown listType="disputes" dispute={dispute} />
        ),
      },
    ],
    nonFalse
  );

  const page = data?.paginatedDisputes;
  let disputes = page?.disputes || [];
  const totalDisputes = page?.totalCount ?? 0;

  disputes = disputes.map((dispute) => {
    let rowClassName;
    if (dispute.needAction) {
      rowClassName = 'need-action-row';
    }
    return { ...dispute, rowClassName };
  });

  const tablePageInfo = useMemo(
    () => ({
      ...pageData,
      showSizeChanger: true,
      defaultPageSize: pageData.limit,
      total: totalDisputes,
    }),
    [pageData, totalDisputes]
  );

  const onFinish = () => {
    setSelectedRowKeys([]);
  };

  const handleTableChange: RenderTableOnChange<DisputeListItemFragmentType> = (
    pagination,
    newFilters,
    sorter
  ) => {
    if (sorter instanceof Array) {
      return;
    }

    const pageSize = pagination.pageSize || tablePageInfo.defaultPageSize;
    const orderDirection = sorter.order === 'ascend' ? 'ASC' : 'DESC';
    const orderMap: Record<string, PaginatedDisputesQueryArgs['orderField']> = {
      employeeName: 'EMPLOYEE_NAME',
      code: 'CODE',
      disputeType: 'DISPUTE_TYPE',
      state: 'STATE',
      supplierName: 'SUPPLIER',
      date: 'DATE',
      employeeId: 'EMPLOYEE_ID',
      differential: 'DIFFERENTIAL',
      lastActionAt: 'LAST_ACTION_AT',
      neededActions: 'NEEDED_ACTIONS',
      invoiceCode: 'INV_CODE',
      revInvoiceCode: 'RINV_CODE',
      costCenter: 'COST_CENTER',
    };
    const orderField =
      orderMap[(sorter.columnKey as string | number) || 'neededActions'] ||
      'NEEDED_ACTIONS';

    if (!isEqual(filters, newFilters)) {
      setSelectedRowKeys([]);
    }

    setFilters((oldFilters) => ({
      ...oldFilters,
      ...newFilters,
    }));
    setPageData(() => ({
      orderField,
      orderDirection,
      offset: ((pagination?.current || 1) - 1) * pageSize,
      limit: pageSize,
    }));
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelection: React.Key[]) => {
      setSelectedRowKeys(newSelection.map((k) => k.toString()));
    },
  };

  const filtersDataReady = !!supplierData;

  const anyError = error || supplierError;

  if (anyError) {
    return <ErrorHandler error={anyError} log="BillingDisputeTable" />;
  }

  return (
    <div className="table-wrapper">
      <TotalCountLabel
        dataLength={disputes.length}
        totalCount={totalDisputes}
        label="disputes"
      />
      <RenderFilteredTable
        rowKey="id"
        className="disputes-table"
        dataSource={disputes}
        columns={disputesColumns}
        loading={loading}
        onChange={handleTableChange}
        pagination={tablePageInfo}
        filters={filters}
        filtersDataReady={filtersDataReady}
        onClearFilter={onClearFilter}
        onClearAllFilters={onClearAllFilters}
        rowSelection={selectable ? rowSelection : undefined}
      />
      {selectedRowKeys.length > 0 && (
        <DisputeBulkActionFooter
          disputeIds={selectedRowKeys}
          onFinish={onFinish}
        ></DisputeBulkActionFooter>
      )}
      <style jsx>{`
        :global(.with-differential-tooltip) {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: space-around;
        }
        :global(.differential-tooltip) {
          padding-left: 6px;
        }
        :global(.disputes-table .ant-table-row) {
          height: 49px;
        }
        :global(.need-action-row) {
          font-weight: bold;
          transition: 0.3s;
        }
        :global(.need-action-row):hover {
          background: ${Colors.elmHyperlight};
          transition: 0.3s;
        }
        :global(tbody .need-action-row td:first-child) {
          border-left: ${Colors.elm} 4px solid;
        }
        :global(
          .disputes-table .need-action-row.ant-table-row-selected td:first-child
        ) {
          border-left: ${Colors.elm} 4px solid;
        }
        :global(.creation-reason-col) {
          max-width: 120px;
        }
      `}</style>
    </div>
  );
};
